// ============================================================================
// MÓDULO: STEP WIZARD — PADRÃO RDA (V1.0)
// ARQUIVO: src/components/StepWizard.jsx
// OBJETIVO: Componente reutilizável de Wizard de Abas Sequenciais.
//           Traduz o padrão da "Ferramenta Editorial — Centro Europeu"
//           para o ecossistema React do Portal Jurídico RDA.
//
// CORES INSTITUCIONAIS (CONSTITUIÇÃO V75.0 — INVIOLÁVEIS):
//   --brand-blue:     #20243B  (Azul Institucional)
//   --highlight-gold: #C5A059  (Champagne Gold)
//   --highlight-red:  #dc2626  (Vermelho — alertas)
//
// USO BÁSICO:
//   <StepWizard
//     title="Gerar Contrato"
//     subtitle="Document Factory · RDA"
//     steps={['1. Selecionar', '2. Configurar', '3. Gerar']}
//   >
//     <StepPanel>...conteúdo da etapa 1...</StepPanel>
//     <StepPanel>...conteúdo da etapa 2...</StepPanel>
//     <StepPanel
//       cta="▶  GERAR DOCUMENTO"
//       onExecute={handleGerar}
//       executing={isProcessing}
//     >
//       ...conteúdo da etapa 3...
//     </StepPanel>
//   </StepWizard>
//
// NAVEGAÇÃO:
//   - Abas anteriores: clicáveis (permite voltar)
//   - Aba ativa: marcada com linha dourada
//   - Abas futuras: bloqueadas (não clicáveis)
//   - Botão "Próximo →" avança para a próxima etapa
//   - Botão "← Anterior" retrocede
//   - CTA no StepPanel substitui o botão "Próximo" na última etapa
// ============================================================================

import React, { Children, createContext, useContext, useState } from 'react';

// ---------------------------------------------------------------------------
// Contexto interno do wizard (compartilha step ativo e total de steps)
// ---------------------------------------------------------------------------
const WizardCtx = createContext({ activeStep: 0, totalSteps: 0, setStep: () => {} });

// ---------------------------------------------------------------------------
// StepWizard — Container principal
// ---------------------------------------------------------------------------
export function StepWizard({ title, subtitle, steps = [], children, className = '', onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const panels     = Children.toArray(children);
  const totalSteps = steps.length || panels.length;

  const goTo   = (i) => { if (i >= 0 && i < totalSteps) setActiveStep(i); };
  const goNext = ()  => goTo(activeStep + 1);
  const goPrev = ()  => goTo(activeStep - 1);

  const isLast = activeStep === totalSteps - 1;
  const activePanel = panels[activeStep] ?? null;

  return (
    <WizardCtx.Provider value={{ activeStep, totalSteps, setStep: goTo }}>
      <div className={`flex flex-col rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white ${className}`}>

        {/* ── CABEÇALHO RDA ──────────────────────────────────────────── */}
        <div className="flex flex-col" style={{ background: '#20243B' }}>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-baseline gap-2">
              <span className="text-white font-bold text-sm tracking-wide">{title}</span>
              {subtitle && (
                <span className="text-xs" style={{ color: '#C5A059' }}>· {subtitle}</span>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 hover:text-white transition-colors text-lg leading-none ml-4"
                aria-label="Fechar"
              >
                ×
              </button>
            )}
          </div>
          {/* Linha dourada de acento */}
          <div className="h-0.5 w-full" style={{ background: '#C5A059' }} />
        </div>

        {/* ── ABAS NUMERADAS ─────────────────────────────────────────── */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
          {steps.map((label, i) => {
            const isDone   = i < activeStep;
            const isActive = i === activeStep;
            const isLocked = i > activeStep;

            return (
              <button
                key={i}
                onClick={() => !isLocked && goTo(i)}
                disabled={isLocked}
                className={[
                  'flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap',
                  'transition-colors duration-150 border-b-2',
                  isActive
                    ? 'border-[#C5A059] text-[#20243B] bg-white'
                    : isDone
                      ? 'border-transparent text-gray-500 hover:text-[#20243B] hover:bg-gray-100 cursor-pointer'
                      : 'border-transparent text-gray-300 cursor-not-allowed',
                ].join(' ')}
              >
                {/* Marcador de etapa */}
                <span className={[
                  'inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold flex-shrink-0',
                  isActive ? 'text-white' : isDone ? 'text-white' : 'text-gray-300',
                ].join(' ')}
                  style={{ background: isActive ? '#20243B' : isDone ? '#C5A059' : '#E5E7EB' }}
                >
                  {isDone ? '✓' : i + 1}
                </span>
                {label}
              </button>
            );
          })}
        </div>

        {/* ── CONTEÚDO DA ETAPA ATIVA ────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 min-h-[200px]">
          {activePanel}
        </div>

        {/* ── RODAPÉ DE NAVEGAÇÃO ────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          {/* Esquerdo: vazio no step 0, Anterior nos demais */}
          {activeStep > 0 ? (
            <button
              type="button"
              onClick={goPrev}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md active:scale-95 transition-all"
            >
              ← Anterior
            </button>
          ) : (
            <div className="w-24" />
          )}

          {/* Indicador de progresso */}
          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === activeStep ? '24px' : '8px',
                  background: i <= activeStep ? '#C5A059' : '#E5E7EB',
                }}
              />
            ))}
            <span className="text-xs text-gray-400 ml-1">
              {activeStep + 1}/{totalSteps}
            </span>
          </div>

          {/* Direito: Cancelar + Próximo sempre juntos */}
          <div className="flex items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md active:scale-95 transition-all"
              >
                Cancelar
              </button>
            )}
            {!isLast && (
              <button
                type="button"
                onClick={goNext}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] rounded-lg shadow-md hover:bg-[#C5A059] hover:border-[#C5A059] active:scale-95 transition-all"
              >
                Próximo →
              </button>
            )}
          </div>
        </div>

      </div>
    </WizardCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// StepPanel — Conteúdo de uma etapa individual
// Props:
//   children   — conteúdo da etapa
//   cta        — texto do botão de ação principal (ex: "▶  GERAR")
//   onExecute  — função chamada ao clicar no CTA
//   executing  — boolean: true enquanto processando (desabilita o botão)
//   ctaColor   — cor de fundo do botão CTA (default: #20243B)
// ---------------------------------------------------------------------------
export function StepPanel({ children, cta, onExecute, executing = false, ctaColor = '#20243B' }) {
  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Conteúdo da etapa */}
      <div className="flex-1">{children}</div>

      {/* Botão CTA (renderizado apenas se `cta` for fornecido) */}
      {cta && (
        <button
          onClick={onExecute}
          disabled={executing}
          className="w-full py-3 text-white font-bold text-sm rounded-lg shadow-md active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: executing ? '#9CA3AF' : ctaColor }}
          onMouseEnter={e => { if (!executing) e.currentTarget.style.background = '#C5A059'; }}
          onMouseLeave={e => { if (!executing) e.currentTarget.style.background = ctaColor; }}
        >
          {executing ? (
            <>
              <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Processando...
            </>
          ) : cta}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// useWizard — Hook para controle externo do step ativo (uso avançado)
// ---------------------------------------------------------------------------
export function useWizard() {
  return useContext(WizardCtx);
}

export default StepWizard;
